'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { DYNAMIC_APPLICATION_SCAN_AUTOMATION_ENABLED_FLAG } from '@terra/shared-contracts-feature-flags';
import { toasterApi } from '@terra/ui-design-system';
import { useTranslations } from 'next-intl';
import { terraProxyApiClient } from '../../../clients/testerClient';
import {
  DYNAMIC_APPLICATION_SCAN_AUTOMATION_QUERY_KEY,
  useDynamicApplicationScanAutomation,
} from '../../../hooks/api/useDynamicApplicationScanAutomation';
import { useFeatureFlags } from '../../../hooks/useFeatureFlags';
import { useProjectStore } from '../../../store/projectStore';
import {
  areDynamicApplicationScanDraftsEqual,
  createDefaultDynamicApplicationScanDraft,
  dynamicApplicationScanDraftFromBaseline,
  isDynamicApplicationScanDraftPersistable,
  toDynamicApplicationScanUpdatePayload,
  type DynamicApplicationScanBaseline,
  type DynamicApplicationScanDraft,
} from './dynamicApplicationScanDraft';

const ACTIVE_RUN_REFETCH_INTERVAL_MS = 10000;

export function useDynamicApplicationScanSettings() {
  const tDynamicScan = useTranslations(
    'designSystem.automation.dynamicApplicationScan',
  );
  const queryClient = useQueryClient();
  const selectedTarget = useProjectStore((state) => state.selectedTarget);
  const applicationId = selectedTarget?.id ?? '';
  const { isFeatureFlagEnabled, isFetched } = useFeatureFlags(
    DYNAMIC_APPLICATION_SCAN_AUTOMATION_ENABLED_FLAG,
  );
  const showDynamicApplicationScan =
    !selectedTarget?.isBackendOnly && (!isFetched || isFeatureFlagEnabled);
  const [draft, setDraft] = useState<DynamicApplicationScanDraft>(
    createDefaultDynamicApplicationScanDraft,
  );
  const [isSaving, setIsSaving] = useState(false);
  const syncedApplicationIdRef = useRef<string | null>(null);
  const { data: automation } = useDynamicApplicationScanAutomation({
    applicationId,
    enabled: showDynamicApplicationScan && Boolean(applicationId),
    refetchInterval: (query) => {
      const activeStatus = query.state.data?.activeRun?.status;

      return activeStatus === 'RUNNING' || activeStatus === 'QUEUED'
        ? ACTIVE_RUN_REFETCH_INTERVAL_MS
        : false;
    },
  });
  const baseline = useMemo<DynamicApplicationScanBaseline | null>(() => {
    const config = automation?.config;

    if (!config) {
      return null;
    }

    return {
      enabled: config.enabled,
      scheduleRule: config.scheduleRule,
      vulnerabilityEnabled: config.vulnerabilityEnabled,
      directoryEnabled: config.directoryEnabled,
      hasUrl: config.hasUrl,
      hasScanTargets: config.hasScanTargets,
      nextRunAt: automation.nextRunAt,
    };
  }, [automation]);
  const hasUnsavedChanges =
    baseline != null &&
    !areDynamicApplicationScanDraftsEqual(
      draft,
      dynamicApplicationScanDraftFromBaseline(baseline),
    );

  useEffect(() => {
    syncedApplicationIdRef.current = null;
    setDraft(createDefaultDynamicApplicationScanDraft());
  }, [applicationId]);

  useEffect(() => {
    if (!applicationId || !baseline || isSaving) {
      return;
    }

    if (syncedApplicationIdRef.current === applicationId && hasUnsavedChanges) {
      return;
    }

    setDraft(dynamicApplicationScanDraftFromBaseline(baseline));
    syncedApplicationIdRef.current = applicationId;
  }, [applicationId, baseline, hasUnsavedChanges, isSaving]);

  const handleChange = useCallback(
    async (next: DynamicApplicationScanDraft) => {
      setDraft(next);

      if (
        !applicationId ||
        !baseline ||
        !isDynamicApplicationScanDraftPersistable(next) ||
        areDynamicApplicationScanDraftsEqual(
          next,
          dynamicApplicationScanDraftFromBaseline(baseline),
        )
      ) {
        return;
      }

      setIsSaving(true);

      try {
        await terraProxyApiClient.updateApplication({
          id: applicationId,
          ...toDynamicApplicationScanUpdatePayload(next),
        });
        await queryClient.invalidateQueries({
          queryKey: [
            DYNAMIC_APPLICATION_SCAN_AUTOMATION_QUERY_KEY,
            applicationId,
          ],
        });
      } catch (error) {
        setDraft(dynamicApplicationScanDraftFromBaseline(baseline));
        toasterApi.error({
          title: tDynamicScan('saveError'),
          description: error instanceof Error ? error.message : undefined,
        });
      } finally {
        setIsSaving(false);
      }
    },
    [applicationId, baseline, queryClient, tDynamicScan],
  );

  return {
    showDynamicApplicationScan,
    draft,
    baseline,
    automation,
    isSaving,
    hasUnsavedChanges,
    handleChange,
  };
}
